import React, { useEffect, useRef, useState } from 'react';
interface RevealProps {
  children: React.ReactNode;
  /** Stagger position inside a list. Capped so the last item never feels late. */
  index?: number;
  className?: string;
  as?: 'div' | 'li' | 'section';
}

/**
 * One short entrance per block, driven by IntersectionObserver + CSS transitions.
 * No animation library ships to the browser, and reduced-motion visitors get the
 * content rendered immediately.
 */
export function Reveal({
  children,
  index = 0,
  className = '',
  as: Component = 'div'
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const prefersReduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setShown(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -6% 0px'
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return React.createElement(Component, {
    ref: ref as React.Ref<never>,
    className: `reveal${shown ? ' reveal-in' : ''} ${className}`,
    style: {
      transitionDelay: `${Math.min(index, 6) * 45}ms`
    }
  }, children);
}